import React, { ReactNode, useState } from "react";
import * as d3 from "d3";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import LineChart, { DatasetRecord, LineChartDataset } from "./chart/LineChart";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { AutoWidgetQueryBehavior, getActiveWidgetRequestsByName, getWidgetResponsesByName, widgetQueryTitleByResponses, WidgetRequest } from "system/widget/Widget";
import ISONumber from "components/formatting/ISONumber";
import WidgetLegend from "components/widgetParts/WidgetLegend";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import { LineChartSettingsState } from "./settings/LineChartSettings";
import { QueryField, QueryFieldTypes, QueryInfo, socialUniverseField } from "system/query/QueryInfo";
import { MakeDrilldownRangeQuery } from "components/widgets/utility/DrilldownQuery";
import { Query } from "system/query/Query";
import { DashboardQueryDrilldownDialog } from "components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog";

export const defaultWidgetRequest: WidgetRequest = {
  name: "line-chart",
  endpoint: "query/agg",
  queryParams: {
    flatten: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL
};

export function BuildDefaultLineChartRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundTimestampField = queryInfo.fields?.find((field:QueryField) => field.type === QueryFieldTypes.timestamp) || false;
  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;
  const requestInitBody = { aggs: {} };

  if (foundTimestampField) {
    requestInitBody.aggs = {
      histogram: {
        op: "histogram",
        field: foundTimestampField.id,
        ...(foundSocialUniverseField && {
          aggs: {
            [socialUniverseField.label]: {
              op: socialUniverseField.id
            }
          }
        }),
      }
    };
  } else {
    return undefined;
  }

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export const defaultSettings: LineChartSettingsState = {
  // Widget Title
  widgetTitleAlignment: "left",
  widgetTitleCustom: "",
  widgetTitleSource: "default",
  // Line
  alignStart: false,
  selectionType: "band",
  showDots: true,
  showTrendEvents: true,
  // X-Axis
  showXAxis: true,
  xTickInterval: "firstLast",
  // Y-Axis
  showYAxis: true,
  yBaseline: "zero",
  yTickInterval: "firstLast",
  // Widget Legend
  legendAlign: {
    horizontal: "center",
    vertical: "bottom"
  },
  legendLayout: "horizontal",
  legendShow: true
}

export function renderTrendEvent(props: {
    color: string | undefined,
    isHovered: boolean,
    record: DatasetRecord,
    x: number,
    y: number,
  }): ReactNode {
  return (
    <g key={`trend-event-${props.record.title}-${props.x}-${props.y}`}>
      <circle
        cx={props.x}
        cy={props.y}
        r={props.isHovered ? 13 : 9}
        fill={props.color}
      />
      <text
        x={props.x}
        y={props.y+1}
        fontWeight="bold"
        fontSize={props.isHovered ? "24px" : "16px"}
        fill="white"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        𝐢
      </text>
    </g>
  );
}

type DrilldownInfo = {
  query: Query,
  queryIds: Array<string>
  title: string,
};

function LineChartWidget(props: WithQueryWidgetProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [drilldownInfo, setDrilldownInfo] = useState<DrilldownInfo | null>(null);

  const apiResponses = getWidgetResponsesByName(props.apiResponses, "line-chart");
  const widgetSettings: LineChartSettingsState = {
    ...defaultSettings,
    ...props.widget?.data?.settings,
    legendAlign: {
      ...defaultSettings.legendAlign,
      ...props.widget.data?.settings?.legendAlign
    }
  };

  const datasets = apiResponses?.map((response, index): LineChartDataset => {
    const histogramKey = Object.keys(response.responseBody)[0];
    const histogram = response.responseBody[histogramKey];
    const color = response.originatingRequest.queryRequestColor;
    const length = histogram.series.length;
    const values = histogram.series[length-1].values;

    const deviation = d3.deviation(values) ?? 1;
    const mean = d3.mean(values) ?? 0;
    
    const records = [];
    for (let i=0; i<values.length; i++) {
      const stdDeviation = (values[i] - mean) / deviation;
      let showTrendEvent = widgetSettings.showTrendEvents && stdDeviation > 1;
      if (i > 0 && values[i-1] > values[i]) {
        // Don't show trend events when trending downward.
        showTrendEvent = false;
      }
      const data: DatasetRecord = {
        histogram: histogram,
        index: i,
        label: histogram.labels[i],
        renderDot: showTrendEvent ? (props) => renderTrendEvent(props) : undefined,
        response,
        title: response.queryDisplayTitle,
        x: histogram.keys[i],
        y: values[i]
      };
      records.push(data);
    }
    const dataset: LineChartDataset = {
      color,
      records: records,
      xAxis: {
        dataKey: "x",
        valueFormatter: (value) => new Date(value).getTime()
      },
      yAxis: {
        dataKey: "y"
      }
    }
    return dataset;
  });

  function dateFormatter(value: number, index: number, domain: [number, number]): string {
    // This is a simple example of a date formatter.
    const dateFormatter = d3.utcFormat("%Y-%m-%d");
    const text = dateFormatter(new Date(value));
    return text;
  }
  function relativeDateFormatter(value: number, index: number, domain: [number, number]): string {
    const totalDuration = domain[1] - domain[0];
    const millisecondsInYear = 1000 * 60 * 60 * 24 * 365;
    if (totalDuration > millisecondsInYear) {
      // If the duration is too great, just show as dates.
      return dateFormatter(value, index, domain);
    }
    // This is a very basic example of a "duration" format.  0 to "x days".
    if (index === 0) {
      return "0";
    }
    const start = new Date(domain[0]);
    const current = new Date(value);
    const tickDuration = value - domain[0];
    const millisecondsInMonth = 1000 * 60 * 60 * 24 * 30;
    let count = 0;
    let unit = "";
    if (tickDuration > millisecondsInMonth) {
      count = d3.timeMonth.count(start, current);
      unit = count > 1 ? "months" : "month";
      return `${count} ${i18n.t(unit)}`;
    }
    const millisecondsInDay = 1000 * 60 * 60 * 24;
    if (tickDuration > millisecondsInDay) {
      count = d3.timeDay.count(start, current);
      unit = count > 1 ? "days" : "day";
      return `${count} ${i18n.t(unit)}`;
    }
    const millisecondsInHour = 1000 * 60 * 60;
    if (tickDuration > millisecondsInHour) {
      count = d3.timeHour.count(start, current);
      unit = count > 1 ? "hours" : "hour";
      return `${count} ${i18n.t(unit)}`;
    }
    const millisecondsInMinute = 1000 * 60;
    if (tickDuration > millisecondsInMinute) {
      count = d3.timeMinute.count(start, current);
      unit = count > 1 ? "minutes" : "minute";
      return `${count} ${i18n.t(unit)}`;
    }
    count = d3.timeSecond.count(start, current);
    unit = count > 1 ? "seconds" : "second";
    return `${count} ${i18n.t(unit)}`;
  }
  function sigFigFormatter(value: number, index: number, domain: [number, number]): string {
    if (value === 0) {
      return `${value}`;
    }
    // Abbreviate number
    const formatter = d3.format(".3s");
    return formatter(value);
  }

  const tickFormatter = widgetSettings.alignStart ? relativeDateFormatter : dateFormatter;
  const widgetQueryTitle = widgetQueryTitleByResponses(props.apiResponses, "line-chart", i18n);

  return (
    <>
      {datasets &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={widgetSettings.widgetTitleAlignment}
              settingsTitle={widgetSettings.widgetTitleSource}
              defaultTitle={i18n.t("widget-types.line-chart.title")}
              customTitle={widgetSettings.widgetTitleCustom}
              widgetQueryTitle={widgetQueryTitle}
            />
          }
        >
          <>          
            <WidgetLegend
              colorGetter={(dataset) => dataset.color }
              data={datasets}
              horizontalAlign={widgetSettings.legendAlign?.horizontal}
              layout={widgetSettings.legendLayout}
              legendStyle={{ paddingBottom: 0 }}
              show={widgetSettings.legendShow}
              valueGetter={(dataset) => dataset.records[0].title}
              verticalAlign={widgetSettings.legendAlign?.vertical}
            >
              <LineChart
                alignStart={widgetSettings.alignStart}
                datasets={datasets}
                margin={{
                  bottom: widgetSettings.showXAxis ? 5 : 0,
                  right: widgetSettings.showYAxis ? 10 : 0,
                  top: 10,
                }}
                onClick={(records) => {
                  const record = records[0];
                  const field = record.response.originatingRequest.requestInit.body.aggs.histogram.field;
                  const currentKey = record.histogram.keys[record.index];
                  const currentLabel = record.histogram.labels[record.index];
                  const nextKey = record.histogram.keys[record.index+1];
                  const nextLabel = record.histogram.labels[record.index+1];
                  // TODO: Both the LineChartWidget and TrendChart only drill down into a single
                  // query.  This needs to be updated to drill down into all queries.  This 
                  // requires the drilldown dialog to support a query selector to be able to toggle
                  // through the different queries.  This feature will be coming after the chart is 
                  // released.
                  const drilldownInfo = MakeDrilldownRangeQuery(field, currentKey, currentLabel, nextKey, nextLabel);
                  const drilldownQueryIds = record.response.originatingRequest.activeQueryIds
                  if (drilldownInfo?.query && drilldownInfo.title) {
                    setDrilldownInfo({
                      query: drilldownInfo?.query,
                      queryIds: drilldownQueryIds,
                      title: drilldownInfo?.title ?? ""
                    });
                  }
                }}
                renderTooltip={(records) => {
                  const recordLabel = records[0]?.label;
                  return (
                    <Box>
                      <Typography variant="h4">{recordLabel}</Typography>
                      {records.map((record, index) => {
                        const color = record.response.originatingRequest.queryRequestColor;
                        return (
                          <Stack key={`${record.label}-${index}`} direction="row" sx={{gap: 1, color}}>
                            <Typography>{record.title}</Typography>
                            <ISONumber number={record.y} sx={{ fontWeight: "bold" }} />
                          </Stack>
                        )
                      })}
                    </Box>
                  )
                }}
                selectionType={widgetSettings.selectionType}
                showDots={widgetSettings.showDots}
                showXAxis={widgetSettings.showXAxis}
                showYAxis={widgetSettings.showYAxis}
                xTickFormatter={tickFormatter}
                xTickInterval={widgetSettings.xTickInterval}
                yBaseline={widgetSettings.yBaseline}
                yTickFormatter={sigFigFormatter}
                yTickInterval={widgetSettings.yTickInterval}
              />
            </WidgetLegend>
            {drilldownInfo &&
              <DashboardQueryDrilldownDialog
                title={drilldownInfo.title}
                queryInfo={props.queryInfo}
                onClose={() => setDrilldownInfo(null)}
                activeQueryIds={drilldownInfo.queryIds || []}
                drilldownQuery={drilldownInfo.query}
                widgetRequestInfo={props.widget?.request_info}
              />
            }
          </>
        </WidgetLayout>
      }
    </>
  )
}

export default withQueryWidget(LineChartWidget, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});